import React, { useCallback } from 'react';
import classnames from 'classnames';
import { noop, map, mean, sum } from 'lodash';
import RadialPieChart from 'js/components/Charts/RadialPieChart';
import DetailData from './DetailData';

import styles from '../DetailsPane.module.scss';

export const OverviewDetailPane = ({
  summaryData,
  filter,
  categoryColorMap,
  paletteMap,
  onSliceOver = noop,
  onSliceClick = noop,
  onSliceOut = noop,
  generateKey = (questionSetKey, questionKey) =>
    `${questionSetKey}-${questionKey}`,
}) => {
  const dataPoints = map(summaryData.questionSets, (qs, qsKey) => {
    return {
      ...qs,
      category: qs.category,
      questionSetKey: qsKey,
      questionKey: null,
      paletteKey: generateKey(qsKey, null),
    };
  });

  const paletteClassName = useCallback(
    ({ questionKey, questionSetKey, paletteKey }) => {
      return classnames(
        paletteMap[paletteKey]?.className,
        filter?.criteria?.questionKey === questionKey &&
          filter?.criteria?.questionSetKey === questionSetKey
          ? undefined
          : 'palette-muted'
      );
    },
    [paletteMap, filter]
  );

  const handleHoverSlice = useCallback(
    ({ questionSetKey, questionKey }) => {
      onSliceOver(questionSetKey, questionKey);
    },
    [onSliceOver]
  );

  const handleSelectSlice = useCallback(
    ({ questionSetKey, questionKey }) => {
      onSliceClick(questionSetKey, questionKey);
    },
    [onSliceClick]
  );

  const handleClearSlices = useCallback(() => {
    onSliceOut();
  }, [onSliceOut]);

  const getDetailData = () => {
    if (filter?.subtype === 'question') {
      const questionSet =
        summaryData.questionSets[filter.criteria.questionSetKey];

      const scores = map(questionSet.questions, 'normalizedScore').filter(
        (x) => x !== null
      );
      const medianScore =
        scores.length > 0
          ? (scores[Math.floor((scores.length - 1) / 2)] +
              scores[Math.ceil((scores.length - 1) / 2)]) /
            2
          : null;
      const meanScore = questionSet.normalizedScore;
      const variance =
        scores.length > 1
          ? sum(scores.map((s) => Math.pow(s - meanScore, 2))) /
            (scores.length - 1)
          : null;

      return {
        titleLabel: 'Question Set',
        title: questionSet.displayName,
        meanScore,
        medianScore,
        stdDev: Math.sqrt(variance),
      };
    }

    // else
    const scores = dataPoints
      .map((d) => d.normalizedScore)
      .filter((x) => x !== null);
    const meanScore = scores.length > 0 ? mean(scores) : null;
    const medianScore =
      scores.length > 0
        ? (scores[Math.floor((scores.length - 1) / 2)] +
            scores[Math.ceil((scores.length - 1) / 2)]) /
          2
        : null;
    const variance =
      scores.length > 1
        ? mean(scores.map((s) => Math.pow(s - meanScore, 2)))
        : null;

    return {
      title: 'Overview summary',
      meanScore,
      medianScore,
      stdDev: Math.sqrt(variance),
    };
  };

  const detailProps = getDetailData();

  return (
    <div>
      <label>Statistics</label>
      <div className={styles['overview-data']}>
        <div className={styles['overview-chart']}>
          <div className="assessment-history-chart">
            <div className="assessment-history-circle-chart">
              <div>
                <RadialPieChart
                  dataPoints={dataPoints}
                  colorMap={categoryColorMap ?? {}}
                  onHoverSlice={handleHoverSlice}
                  onClickSlice={handleSelectSlice}
                  onClearSlices={handleClearSlices}
                  paletteClassName={paletteClassName}
                />
              </div>
            </div>
          </div>
        </div>
        <DetailData {...detailProps} />
      </div>
    </div>
  );
};

export default OverviewDetailPane;
